import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Donation } from '../firebase/donationService';
import {
  subscribeToDonationStats,
  subscribeToRecentDonations
} from '../firebase/realtimeDonationService';

// Define the shape of our context state
interface DonationContextState {
  totalCollected: number;
  targetAmount: number;
  donationCount: number;
  pendingDonations: number;
  recentDonations: Donation[];
  isLoading: boolean;
}

// Create the context with default values
const DonationContext = createContext<DonationContextState>({
  totalCollected: 0,
  targetAmount: 100000,
  donationCount: 0,
  pendingDonations: 0,
  recentDonations: [],
  isLoading: true
});

interface DonationProviderProps {
  children: ReactNode;
}

export const DonationProvider: React.FC<DonationProviderProps> = ({ children }) => {
  const [stats, setStats] = useState({
    totalCollected: 0,
    targetAmount: 100000,
    donationCount: 0,
    pendingDonations: 0
  });
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Subscribe to real-time updates for donation stats
    const statsUnsubscribe = subscribeToDonationStats((newStats) => {
      setStats(newStats);
      setIsLoading(false);
    });

    // Subscribe to real-time updates for Recent Offers
    const donationsUnsubscribe = subscribeToRecentDonations((donations) => {
      setRecentDonations(donations);
    });

    // Clean up subscriptions on unmount
    return () => {
      statsUnsubscribe();
      donationsUnsubscribe();
    };
  }, []);

  const value = {
    ...stats,
    recentDonations,
    isLoading
  };

  return (
    <DonationContext.Provider value={value}>
      {children}
    </DonationContext.Provider>
  );
};

// Custom hook for using the donation context
export const useDonationData = () => useContext(DonationContext);
